import React, { FC, useEffect, useState } from 'react';
import WallCard from './WallCard';
import { debounce } from 'lodash';
import { Item, ItemType } from 'src/interfaces/api/ActivityWall';
import useActivityWall from 'src/hooks/api/useActivityWall';
import { Order } from 'src/interfaces/Table';
import useCache from 'src/hooks/useCache';
import useEmployeeAccess from 'src/hooks/api/useEmployeeAccess';
import Employee from 'src/interfaces/api/Employee';
import { useRecoilState } from 'recoil';
import employeesState from 'src/recoil/atoms/employees';

interface Props {
  wallId: string;
}

const WallCardContainer: FC<Props> = ({ wallId, ...props }) => {
  const [items, setItems] = useCache<Item[]>();
  const [, setEmployees] = useRecoilState(employeesState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [startDateFilter, setStartDateFilter] = useState<string | null>(null);
  const [endDateFilter, setEndDateFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ItemType>();
  const [search, setSearch] = useState<string>();
  const [orderBy] = useState('createdAt');
  const [order, setOrder] = useState<Order>('desc');

  const { getItems } = useActivityWall();
  const { getAll: getAllEmployee } = useEmployeeAccess();

  useEffect(() => {
    getAllEmployee().then((collection) => {
      const employeesById: Record<Employee['id'], Employee> = Object.assign(
        {},
        ...collection.items.map((employee) => ({ [employee.id]: employee }))
      );

      setEmployees(employeesById);
    });
  }, []);

  useEffect(() => {
    setLoading(true);

    getItems(wallId, { search, type: typeFilter, startDate: startDateFilter, endDate: endDateFilter }, orderBy, order)
      .then((collection) => {
        setItems(collection.items);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [wallId, search, typeFilter, order, orderBy, startDateFilter, endDateFilter]);

  const handleChangeSearchString = debounce((searchString: string) => {
    setSearch(searchString);
  }, 600);

  const handleDateFilterChange = (startDate: string | null, endDate: string | null, order: Order) => {
    setStartDateFilter(startDate);
    setEndDateFilter(endDate);
    setOrder(order);
  };

  if (error) {
    return <div style={{ color: '#f44336' }}>{error}</div>;
  }

  return (
    <WallCard
      search={search}
      order={order}
      typeFilter={typeFilter}
      onTypeFilterChange={setTypeFilter}
      startDateFilter={startDateFilter}
      endDateFilter={endDateFilter}
      onDateFilterChange={handleDateFilterChange}
      onChangeSearchString={handleChangeSearchString}
      loading={loading}
      items={items ?? []}
      wallId={wallId}
      {...props}
    />
  );
};

export default React.memo(WallCardContainer, (prevProps, nextProps) => prevProps.wallId === nextProps.wallId);
